/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { DeviceModel, DeviceFeatures } from "../../model/device";
import {
  Log,
  PostSigninFlowEvent,
  PostSigninLogger,
  PostSigninStartupResult,
  PostSigninStartupStep,
  Sage
} from "../../model/logging";
import { InternalRatingsLogger } from "../../model/logging/InternalRatingsLogger";
import { PostSigninInteractor } from "../../model/interactor";
import { RetryMechanism } from "../../model/retrymechanism";
import { ErrorTypes } from "../../model/error";
import { DiagnosticSeverity, RETRY_POLICY } from "../../model/types";
import type { TNetworkOptions } from "../../model/types/TNetworkOptions";
import { formatRetryErrorMessage } from "../../model/utility";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import {
  fetchQuickMenuShortcuts,
  setNpvrRecordingAllowed
} from "../homescreen/homescreenSlice";
import { fetchBrandingUiBundle, selectEasDisplayMode } from "../branding";
import { showAlertOverlay, dismissAlertOverlayIfOwned } from "../alertOverlay";
import {
  buildConnectionRetryActions,
  CONNECTION_WARMUP_RETRY_DELAYS_MS,
  getRetryNowSignal,
  isConnectionWarmupAttempt
} from "../connectionRetry";
import {
  loadProfiles,
  loadActiveProfileSelectionPreference,
  activateProfile,
  isProfileError,
  PROFILE_ERROR_OPERATIONS,
  showProfileErrorDialog,
  runProfileScopedFetches,
  type TActiveProfileSelection,
  type TProfileResponse
} from "../profiles";
import type {
  PostSigninStartupState,
  TPostSigninStartupParams
} from "./postSigninStartup.types";
import { DrmManager } from "../../model/videoplayer/impls/drm/DrmManager";
import { EamModelImpl } from "../../model/pushNotification";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";

const TAG: string = "postSigninStartupSlice";

/**
 * Owner tag for the blocking overlay raised by {@link callCritical}, so only
 * that retry can take it down. See `dismissAlertOverlayIfOwned`.
 */
const CRITICAL_RETRY_OVERLAY_OWNER: string = "post-signin-critical-retry";

/**
 * The catalog code a mapped service error carries, or "" for anything else.
 * `AppError` does not extend `Error`, so it is detected by its accessor.
 */
function readErrorCode(error: unknown): string {
  if (error === null || error === undefined || typeof error !== "object") {
    return "";
  }
  const candidate = error as { getCode?: () => string };
  if (typeof candidate.getCode !== "function") {
    return "";
  }
  const code: unknown = candidate.getCode();
  return typeof code === "string" ? code : "";
}

/** Action ids on the escape-hatch overlay, handled by `startupRecovery.listeners`. */
export const StartupRecoveryAction = {
  /** Re-boot the app: re-fetches the TSRI catalog, picking up a server fix. */
  RETRY: "startup-retry"
} as const;

/**
 * Network options for the calls this sequence makes.
 *
 * The sequence is session-scoped, not screen-scoped: it keeps running across
 * the redirect chain that follows sign-in (`/signin` → `/home` → optionally
 * `/select-profile`). Every internal route change calls
 * `NetworkGuard.cancelAllForNavigation()`, which would otherwise abort
 * whichever startup call is in flight at that moment — the failure is
 * swallowed by the per-step try/catch and startup silently completes with
 * missing device features, branding, or profiles.
 */
const SESSION_SCOPED_CALL: TNetworkOptions = {
  bypassNavigationCancellation: true
};

/**
 * Records that a device-scoped startup step has completed.
 *
 * Emitted from inside the sequence rather than carried on its `fulfilled`
 * payload because the sequence can return early at the profile gate, long
 * before it fulfils — and the resume run needs to know these steps are done
 * precisely in that case.
 *
 * `branding.isLoaded` is deliberately not reused for the bundle: it is also
 * raised by `setPartnerLogoUrl`, which `index.tsx` dispatches at boot from the
 * persisted logo, so it reports "a logo is available", not "the bundle was
 * fetched".
 */
export const deviceFeaturesLoaded = createAction(
  "postSigninStartup/deviceFeaturesLoaded"
);
export const brandingBundleLoaded = createAction(
  "postSigninStartup/brandingBundleLoaded"
);
export const stringOverridesLoaded = createAction<Record<string, string>>(
  "postSigninStartup/stringOverridesLoaded"
);

const INITIAL_STATE: PostSigninStartupState = {
  isStarting: false,
  hasStartedUp: false,
  startupError: null,
  inHome: null,
  inRegion: null,
  inBlacklist: null,
  requiresProfileSelection: false,
  isProfileGateResolved: false,
  hasLoadedDeviceFeatures: false,
  hasLoadedBrandingBundle: false,
  hasLoadedStringOverrides: false,
  stringOverrides: {},
  // Internal ratings state
  isLoadingInternalRatings: false,
  isInternalRatingsLoaded: false,
  internalRatingsError: ""
};

/**
 * Post sign-in startup sequence — runs after a successful sign-in on every app boot.
 *
 * The call order below is a product requirement, not an implementation detail:
 *
 *  1. serviceEndpoints            — everything downstream resolves its basePath here
 *  2. deviceFeatures
 *  3. brandingUiBundle
 *  4. profiles                    — profile gate, ahead of any profile-scoped call
 *  5. stationPolicies ┐ the profile-scoped set — `PROFILE_SCOPED_FETCHES`, run
 *  6. channels        ┘ in order; both come from one `fetchChannels()` call
 *  7. quickMenuShortcuts          — dispatched here, resolves off the critical path
 *  8. npvrAllowRecording          — dispatched here, resolves off the critical path
 *  9. myWanIpAddress
 * 10. inHomeLocation
 * 11. videoProviderConfigurations ┐ `fetchVideoProviderConfiguration()` acquires a
 * 12. scopedDomainToken           ┘ token per Widevine config it just resolved
 * 13. internalRatings
 * 14. softClientCert              — acquired by the WebSocket FSM's GET_CLIENT_CERT
 *                                   delegate, so `connectWebSocket()` closes the run
 *
 * Every step owns its try/catch: a failure is logged and the sequence continues,
 * so one dead service cannot strand the user on the splash screen.
 *
 * **Step 2 is the deliberate exception.** It runs under `callCritical`, whose
 * CRITICAL policy never gives up, so a `deviceFeatures` outage parks the
 * sequence there indefinitely behind a blocking overlay and Steps 3-14 never
 * run. Its `catch` is unreachable for that reason, and kept only so a future
 * `isTerminal` predicate or abort signal cannot silently fall through. The
 * response carries the `msoServiceId` every msoServiceId-scoped service below
 * needs; continuing without it does not degrade gracefully, it just relocates
 * the failure to every screen at once.
 *
 * Routing does not wait on any of this — `ProtectedRoute` renders Home/Player
 * as soon as the user is authenticated — so the blocked screen is live behind
 * the overlay and issuing its own endpoint lookups. Two consequences:
 *
 *  - The overlay is owner-tagged, so a background TSRI endpoint recovery on
 *    that screen cannot dismiss a block this step still holds. See
 *    {@link CRITICAL_RETRY_OVERLAY_OWNER}.
 *  - Steps 4 and 7 are the sole owners of the profile gate and the quick-menu
 *    shortcuts. `MainLayout` and `NavLayout` used to fetch both again on
 *    mount, which raced these steps rather than standing in for them: the
 *    layouts mount immediately, so their copies went out first — before the
 *    `msoServiceId` this step supplies existed. They now read what these steps
 *    produce, and only act themselves once `hasStartedUp` shows the sequence
 *    finished without producing it.
 *
 * TODO/Future work: get body info, and load parental control settings.
 */
export const postSigninStartupSequence = createAsyncThunk<
  Pick<
    PostSigninStartupState,
    | "inHome"
    | "inRegion"
    | "inBlacklist"
    | "requiresProfileSelection"
    | "isProfileGateResolved"
  >,
  TPostSigninStartupParams | undefined,
  {
    dispatch: UnityAppDispatch;
    state: UnityRootState;
    rejectValue: string;
  }
>(
  "postSigninStartup/postSigninStartupSequence",
  // This orchestrator intentionally coordinates ordered critical and non-fatal startup steps.
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async (params, { dispatch, getState }) => {
    const interactor: PostSigninInteractor = new PostSigninInteractor();
    const startupTransactionId: string = PostSigninLogger.logStartupStart({
      entry: params !== undefined && params.isRootEntry ? "root" : "resume"
    });
    const failedSteps: string[] = [];
    const forceProfileSelection: boolean =
      params !== undefined && params.forceProfileSelection === true;
    const isRootEntry: boolean =
      params !== undefined && params.isRootEntry === true;
    let requiresProfileSelection: boolean = false;
    let isProfileGateResolved: boolean = false;
    let hasLoggedStartupBlocked: boolean = false;

    const logStepStarted = (step: string): void => {
      PostSigninLogger.logFlowEvent(
        PostSigninFlowEvent.StepStarted,
        "Post sign-in startup step started",
        DiagnosticSeverity.Info,
        { step }
      );
    };
    const logStepCompleted = (step: string): void => {
      PostSigninLogger.logFlowEvent(
        PostSigninFlowEvent.StepCompleted,
        "Post sign-in startup step completed",
        DiagnosticSeverity.Info,
        { step }
      );
    };
    const logStepSkipped = (step: string): void => {
      PostSigninLogger.logFlowEvent(
        PostSigninFlowEvent.StepSkipped,
        "Post sign-in startup step skipped",
        DiagnosticSeverity.Info,
        { step }
      );
    };
    const logStepFailure = (step: string, error: unknown): void => {
      failedSteps.push(step);
      PostSigninLogger.logFlowFailure(
        PostSigninFlowEvent.StepFailed,
        "Post sign-in startup step failed",
        error,
        { step }
      );
    };
    const completeStartup = (result: string): void => {
      PostSigninLogger.logFlowEvent(
        PostSigninFlowEvent.StartupCompleted,
        "Post sign-in startup completed",
        DiagnosticSeverity.Info,
        { result, failed_steps: failedSteps.join(",") || undefined }
      );
      PostSigninLogger.logStartupEnd(startupTransactionId, {
        result,
        failed_steps: failedSteps.join(",") || undefined
      });
    };

    // Loads the saved active-profile preference. A read failure is not fatal:
    // it degrades to "no valid selection", which sends the user to the picker.
    const loadPreference =
      async (): Promise<TActiveProfileSelection | null> => {
        try {
          return await dispatch(
            loadActiveProfileSelectionPreference()
          ).unwrap();
        } catch (preferenceError: unknown) {
          Log.e(
            TAG,
            "resolveProfileGate() loadActiveProfileSelectionPreference()",
            preferenceError
          );
          return null;
        }
      };

    const activateProfileForStartup = async (
      profileId: string,
      isSkipSelectedProfileEnabled: boolean
    ): Promise<void> => {
      await dispatch(
        activateProfile({ profileId, isSkipSelectedProfileEnabled })
      ).unwrap();
    };

    /**
     * The profile list the gate reasons over, fetched only when the store does
     * not already hold one.
     *
     * The resume run after profile selection re-enters the gate having already
     * loaded the list on the first pass, and re-issuing `GET /profile` there is
     * a duplicate: the store still holds the response, and every screen that
     * mutates the list (Manage Profiles, Profile Details, the delete flow)
     * re-dispatches `loadProfiles` itself, so what is cached is current.
     */
    const loadProfileList = async (): Promise<
      TProfileResponse[] | null | undefined
    > => {
      const profilesState = getState().profiles;
      const cached =
        profilesState !== undefined && profilesState !== null
          ? profilesState.profilesResponse
          : null;
      if (cached !== null && cached !== undefined) {
        Log.d(TAG, "resolveProfileGate() reusing the loaded profile list");
        return cached.profiles;
      }
      const response = await dispatch(loadProfiles()).unwrap();
      return response.profiles;
    };

    // Sole profile and no valid saved selection → pick it on the user's behalf.
    const autoSelectSoleProfile = async (
      profiles: TProfileResponse[]
    ): Promise<void> => {
      const firstProfile = profiles[0];
      if (firstProfile === undefined) {
        return;
      }
      await activateProfileForStartup(firstProfile.profileId, false);
    };

    // Resolves the profile gate:
    //   - An explicit picker entry always waits for user selection.
    //   - A valid saved selection is used on non-root entries.
    //   - Root uses a valid saved selection only when skip is enabled.
    //   - A sole profile is auto-selected when user choice is unnecessary.
    //   - Otherwise, multiple profiles require /select-profile.
    const resolveProfileGate = async (): Promise<{
      requiresProfileSelection: boolean;
      isProfileGateResolved: boolean;
    }> => {
      try {
        const profiles = await loadProfileList();
        if (profiles === null || profiles === undefined) {
          return {
            requiresProfileSelection: false,
            isProfileGateResolved: false
          };
        }

        if (forceProfileSelection) {
          DeviceModel.getTiVoSettings().setProfileId("");
          return {
            requiresProfileSelection: true,
            isProfileGateResolved: true
          };
        }

        const preference: TActiveProfileSelection | null =
          await loadPreference();

        const profilesState = getState().profiles;
        const hasActiveSelection: boolean =
          profilesState !== undefined &&
          profilesState.isProfileSelected &&
          profilesState.selectedProfileId.length > 0;

        if (hasActiveSelection) {
          return {
            requiresProfileSelection: false,
            isProfileGateResolved: true
          };
        }

        const hasPersistedProfileId: boolean =
          preference !== null && preference.activeProfileId !== "";
        const persistedProfileExists: boolean = profiles.some(
          (profile: TProfileResponse): boolean =>
            hasPersistedProfileId &&
            preference !== null &&
            profile.profileId === preference.activeProfileId
        );
        const shouldUsePersistedProfile: boolean =
          persistedProfileExists &&
          preference !== null &&
          (preference.isSkipSelectedProfileEnabled || !isRootEntry);

        if (shouldUsePersistedProfile && preference !== null) {
          await activateProfileForStartup(
            preference.activeProfileId,
            preference.isSkipSelectedProfileEnabled
          );
          return {
            requiresProfileSelection: false,
            isProfileGateResolved: true
          };
        }

        if (profiles.length === 1) {
          await autoSelectSoleProfile(profiles);
          return {
            requiresProfileSelection: false,
            isProfileGateResolved: true
          };
        }

        if (profiles.length > 1) {
          // Clear the stale in-memory profileId so requests made before the
          // user completes profile selection do not carry the previous session's ID.
          DeviceModel.getTiVoSettings().setProfileId("");
          return {
            requiresProfileSelection: true,
            isProfileGateResolved: true
          };
        }

        return {
          requiresProfileSelection: false,
          isProfileGateResolved: true
        };
      } catch (error: unknown) {
        Log.e(TAG, "resolveProfileGate()", error);
        if (isProfileError(error)) {
          dispatch(
            showProfileErrorDialog({
              error: error,
              onRetry: (): void => {
                dispatch(postSigninStartupSequence(params)).catch(
                  (retryError: unknown): void => {
                    Log.e(TAG, "resolveProfileGate() retry", retryError);
                  }
                );
              },
              operation: PROFILE_ERROR_OPERATIONS.LOAD
            })
          );
        }
        return {
          requiresProfileSelection: false,
          isProfileGateResolved: false
        };
      }
    };

    /**
     * Runs a step the app cannot function without: retried on the CRITICAL
     * policy behind a blocking "Connection Error" overlay until it succeeds.
     *
     * CRITICAL never gives up — `RetryMechanism` has no throw path for it
     * absent an `isTerminal` predicate or an abort signal, neither of which is
     * passed here — so the sequence parks on this step for as long as the
     * service is down. That is deliberate for Step 2: its response carries the
     * `msoServiceId` every msoServiceId-scoped service below needs, so
     * continuing without it just moves the failure downstream, one service at
     * a time.
     *
     * The overlay is tagged with {@link CRITICAL_RETRY_OVERLAY_OWNER} and
     * dismissed by owner, so a background TSRI endpoint recovery cannot take
     * down a block this step is still holding.
     *
     * From the first failure the overlay carries Exit (TV only) / Retry —
     * Retry wakes this loop through {@link getRetryNowSignal} so the failed
     * call is re-issued now.
     */
    const callCritical = async <T>(call: () => Promise<T>): Promise<T> => {
      return RetryMechanism.callServiceWithRetry(call, [], {
        retryPolicy: RETRY_POLICY.CRITICAL,
        initialRetryDelaysMs: CONNECTION_WARMUP_RETRY_DELAYS_MS,
        getRetryNowSignal: getRetryNowSignal,
        /**
         * `FE.BAD_REQUEST` (400) is the one Step 2 failure re-issuing the same
         * request cannot fix — the device sent something the gateway rejects.
         * It fails the step instead of parking startup behind the overlay; the
         * step is retried from scratch on the next startup run, since only a
         * successful load sets `hasLoadedDeviceFeatures`.
         *
         * Everything else keeps retrying on purpose, including the 500 the
         * schema marks "Retry: no": the response carries msoServiceId, and
         * without it every scoped service below fails too. A 401 never reaches
         * here — `executeGuarded` refreshes the token and replays it first.
         */
        isTerminal: (error: unknown): boolean => {
          const terminal: boolean =
            readErrorCode(error) ===
            ErrorTypes.FE_PROVISIONING_BAD_REQUEST_A600;
          if (terminal) {
            dispatch(
              dismissAlertOverlayIfOwned({
                owner: CRITICAL_RETRY_OVERLAY_OWNER,
                dismissedByActionId: "retry-terminal"
              })
            );
          }
          return terminal;
        },
        onRetry: (error: unknown, attempt: number): void => {
          // Retry silently at [1s, 3s, 5s] before surfacing the overlay.
          if (isConnectionWarmupAttempt(attempt)) {
            return;
          }
          if (!hasLoggedStartupBlocked) {
            hasLoggedStartupBlocked = true;
            PostSigninLogger.logFlowFailure(
              PostSigninFlowEvent.StartupBlocked,
              "Post sign-in startup blocked by critical retry",
              error,
              { step: PostSigninStartupStep.DeviceFeatures }
            );
            PostSigninLogger.logStartupBlocked({
              step: PostSigninStartupStep.DeviceFeatures
            });
          }
          dispatch(
            showAlertOverlay({
              title: "Connection Error",
              message: formatRetryErrorMessage(error),
              actions: buildConnectionRetryActions(),
              owner: CRITICAL_RETRY_OVERLAY_OWNER
            })
          );
        },
        onSuccess: (): void => {
          dispatch(
            dismissAlertOverlayIfOwned({
              owner: CRITICAL_RETRY_OVERLAY_OWNER,
              dismissedByActionId: "retry-recovered"
            })
          );
        }
      });
    };

    // Step 1 — publish the Sage HEC URLs from the TSRI endpoint cache.
    //
    // No fetch here: TSRI is not body-scoped, so the single pre-sign-in
    // `appStartup/fetchGlobalEndpoints` call has already populated the cache
    // with every endpoint. This step only reads it, so it cannot fail and
    // needs no retry wrapper.
    Log.d(
      TAG,
      "postSigninStartupSequence() — Step 1: publishing Sage HEC URLs"
    );
    PostSigninLogger.logFlowEvent(
      PostSigninFlowEvent.StartupStarted,
      "Post sign-in startup started",
      DiagnosticSeverity.Info
    );
    logStepStarted(PostSigninStartupStep.SageEndpoints);
    Sage.populateTsriEndpoints();
    logStepCompleted(PostSigninStartupStep.SageEndpoints);
    Log.d(TAG, "postSigninStartupSequence() — Step 1 complete");

    // Step 2 — load device features. Critical, on the same footing as Step 1:
    // the response carries msoServiceId, which every msoServiceId-scoped
    // service below needs. `loadDeviceFeatures()` rejects when the value is
    // absent, so a provisioning gap retries here rather than letting those
    // services go out with an empty msoServiceId and fail one by one.
    //
    // Skipped on the resume run: the feature set is device- and MSO-scoped, so
    // picking a profile cannot change it, and the domain-lib service still
    // holds the features the first run cached. Only a *successful* load sets
    // the flag, so a failed one is retried rather than silently skipped.
    if (getState().postSigninStartup.hasLoadedDeviceFeatures) {
      logStepSkipped(PostSigninStartupStep.DeviceFeatures);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 2 skipped, device features already loaded"
      );
    } else {
      try {
        logStepStarted(PostSigninStartupStep.DeviceFeatures);
        Log.d(
          TAG,
          "postSigninStartupSequence() — Step 2: loading device features"
        );
        await callCritical(() =>
          interactor.loadDeviceFeatures(SESSION_SCOPED_CALL)
        );
        dispatch(deviceFeaturesLoaded());
        logStepCompleted(PostSigninStartupStep.DeviceFeatures);
        Log.d(TAG, "postSigninStartupSequence() — Step 2 complete");
      } catch (error: unknown) {
        logStepFailure(PostSigninStartupStep.DeviceFeatures, error);
        Log.e(TAG, "postSigninStartupSequence() — Step 2 failed", error);
      }
    }

    // Step 3 — fetch branding UI bundle (non-fatal).
    //
    // Skipped on the resume run for the same reason as Step 2: the bundle is
    // keyed by MSO partner and service, not by profile. Only a successful
    // fetch sets the flag, so a bundle that failed the first time is attempted
    // again here.
    if (getState().postSigninStartup.hasLoadedBrandingBundle) {
      logStepSkipped(PostSigninStartupStep.BrandingBundle);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 3 skipped, branding bundle already loaded"
      );
    } else {
      try {
        logStepStarted(PostSigninStartupStep.BrandingBundle);
        Log.d(
          TAG,
          "postSigninStartupSequence() — Step 3: fetching branding UI bundle"
        );
        await dispatch(fetchBrandingUiBundle()).unwrap();
        dispatch(brandingBundleLoaded());
        logStepCompleted(PostSigninStartupStep.BrandingBundle);
        Log.d(TAG, "postSigninStartupSequence() — Step 3 complete");
      } catch (brandingError: unknown) {
        logStepFailure(PostSigninStartupStep.BrandingBundle, brandingError);
        Log.e(
          TAG,
          "postSigninStartupSequence() — Step 3 failed (non-fatal)",
          brandingError
        );
      }
    }

    // Step 3b — fetch string overrides (non-fatal).
    //
    // Sibling of Step 3: same device-configuration-service, same
    // msoPartner/msoService scoping, so skipped on the resume run for the
    // same reason.
    if (getState().postSigninStartup.hasLoadedStringOverrides) {
      logStepSkipped(PostSigninStartupStep.StringOverrides);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 3b skipped, string overrides already loaded"
      );
    } else {
      try {
        logStepStarted(PostSigninStartupStep.StringOverrides);
        Log.d(
          TAG,
          "postSigninStartupSequence() — Step 3b: fetching string overrides"
        );
        const stringOverridesResult =
          await interactor.fetchStringOverrides(SESSION_SCOPED_CALL);
        dispatch(stringOverridesLoaded(stringOverridesResult.overrides));
        logStepCompleted(PostSigninStartupStep.StringOverrides);
        Log.d(TAG, "postSigninStartupSequence() — Step 3b complete");
      } catch (stringOverridesError: unknown) {
        logStepFailure(
          PostSigninStartupStep.StringOverrides,
          stringOverridesError
        );
        Log.e(
          TAG,
          "postSigninStartupSequence() — Step 3b failed (non-fatal)",
          stringOverridesError
        );
      }
    }

    // Step 4 — load profiles and resolve the profile gate. Runs ahead of every
    // profile-scoped call below so Select Profile and HomeScreen have profile
    // data ready as early as the sequence allows. Self-contained error
    // handling: resolveProfileGate() never throws.
    Log.d(TAG, "postSigninStartupSequence() — Step 4: resolving profile gate");
    const profileGateResolution = await resolveProfileGate();
    requiresProfileSelection = profileGateResolution.requiresProfileSelection;
    isProfileGateResolved = profileGateResolution.isProfileGateResolved;
    Log.d(TAG, "postSigninStartupSequence() — Step 4 complete");

    if (requiresProfileSelection) {
      PostSigninLogger.logFlowEvent(
        PostSigninFlowEvent.ProfileSelectionRequired,
        "Post sign-in startup awaiting profile selection",
        DiagnosticSeverity.Info
      );
      completeStartup(PostSigninStartupResult.AwaitingProfile);
      return {
        inHome: null,
        inRegion: null,
        inBlacklist: null,
        requiresProfileSelection,
        isProfileGateResolved
      };
    }

    // Steps 5 and 6 — the profile-scoped data set: station policies and
    // channels today, whatever `PROFILE_SCOPED_FETCHES` lists tomorrow.
    //
    // Defined over in `profiles/profileScopedData` rather than inline here
    // because the same set has to be re-requested on every later profile
    // switch, when this sequence does not run at all. One list, two callers,
    // so a service added for a switch cannot be forgotten at startup or the
    // other way round. Entries fail independently, exactly as this step did.
    Log.d(
      TAG,
      "postSigninStartupSequence() — Steps 5-6: fetching profile-scoped data"
    );
    await runProfileScopedFetches({
      dispatch: dispatch as UnityAppDispatch,
      postSigninInteractor: interactor
    });
    Log.d(TAG, "postSigninStartupSequence() — Steps 5-6 complete");

    // Step 7 — pre-fetch quick menu shortcuts (non-blocking / fire-and-forget).
    // The request is issued here, in sequence; only its completion is allowed
    // to land later, populating homescreen Redux state before HomeScreen mounts.
    logStepStarted(PostSigninStartupStep.QuickMenuShortcuts);
    dispatch(fetchQuickMenuShortcuts());
    Log.d(TAG, "postSigninStartupSequence() — Step 7 dispatched");

    // Step 8 — NPVR recording status (non-blocking / fire-and-forget), issued
    // in sequence for the same reason as Step 7.
    logStepStarted(PostSigninStartupStep.NpvrAllowRecording);
    DeviceFeatures.getNpvrAllowedRecording(interactor, SESSION_SCOPED_CALL)
      .then((isAllowed: boolean) => {
        dispatch(setNpvrRecordingAllowed(isAllowed));
        logStepCompleted(PostSigninStartupStep.NpvrAllowRecording);
      })
      .catch((error: unknown) => {
        logStepFailure(PostSigninStartupStep.NpvrAllowRecording, error);
        Log.e(
          TAG,
          "postSigninStartupSequence() — Step 8 failed (non-fatal)",
          error
        );
      });

    // Step 9 — fetch WAN IP address (non-fatal).
    // Persisted on DeviceModel for downstream consumers (e.g. QoE logging).
    // Failures are logged but never reject the post sign-in sequence.
    try {
      logStepStarted(PostSigninStartupStep.WanIpAddress);
      Log.d(TAG, "postSigninStartupSequence() — Step 9: fetching WAN IP");
      const wanIpResponse = await interactor.fetchWanIp(
        { originRequestId: DeviceModel.getDeviceUuid() },
        SESSION_SCOPED_CALL
      );
      const wanIp: string = wanIpResponse.ipAddress || "";
      DeviceModel.setWanIpAddress(wanIp);
      logStepCompleted(PostSigninStartupStep.WanIpAddress);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 9 complete, hasValue=" +
          String(wanIp.length > 0)
      );
    } catch (wanIpError: unknown) {
      logStepFailure(PostSigninStartupStep.WanIpAddress, wanIpError);
      Log.e(
        TAG,
        "postSigninStartupSequence() — Step 9 failed (non-fatal)",
        wanIpError
      );
    }

    // Step 10 — load in-home location info (non-fatal).
    let inHome: boolean | null = null;
    let inRegion: boolean | null = null;
    let inBlacklist: boolean | null = null;
    try {
      logStepStarted(PostSigninStartupStep.InHomeLocation);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 10: fetching in-home location"
      );
      const result = await interactor.fetchInHomeLocation(
        { msoPartnerId: await DeviceModel.getMsoPartnerId() },
        SESSION_SCOPED_CALL
      );
      inHome = result.inHome === undefined ? null : result.inHome;
      inRegion = result.inRegion === undefined ? null : result.inRegion;
      inBlacklist =
        result.inBlacklist === undefined ? null : result.inBlacklist;
      logStepCompleted(PostSigninStartupStep.InHomeLocation);
      Log.d(TAG, "postSigninStartupSequence() — Step 10 complete");
    } catch (inHomeError: unknown) {
      logStepFailure(PostSigninStartupStep.InHomeLocation, inHomeError);
      Log.e(
        TAG,
        "postSigninStartupSequence() — Step 10 failed (non-fatal)",
        inHomeError
      );
    }

    // Steps 11 and 12 — video provider configurations, then a scoped domain
    // token per Widevine configuration returned. DrmManager issues both in that
    // order; failures MUST NOT fail startup, so they are logged and swallowed.
    try {
      logStepStarted(PostSigninStartupStep.VideoProviderConfiguration);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Steps 11-12: fetching " +
          "videoProviderConfigurations and scoped domain tokens"
      );
      const DrmManagerIns = DrmManager.getInstance();
      await DrmManagerIns.fetchVideoProviderConfiguration();
      logStepCompleted(PostSigninStartupStep.VideoProviderConfiguration);
      Log.d(TAG, "postSigninStartupSequence() — Steps 11-12 complete");
    } catch (error: unknown) {
      logStepFailure(PostSigninStartupStep.VideoProviderConfiguration, error);
      Log.e(
        TAG,
        "postSigninStartupSequence() — Steps 11-12 failed (non-fatal)",
        error
      );
    }

    // Step 13 — load internal ratings (non-fatal).
    try {
      logStepStarted(PostSigninStartupStep.InternalRatings);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 13: fetching internal ratings"
      );
      await interactor.fetchInternalRatings(SESSION_SCOPED_CALL);
      logStepCompleted(PostSigninStartupStep.InternalRatings);
      Log.d(TAG, "postSigninStartupSequence() — Step 13 complete");
    } catch (error: unknown) {
      logStepFailure(PostSigninStartupStep.InternalRatings, error);
      InternalRatingsLogger.logLookupFailed(error);
      Log.e(
        TAG,
        "postSigninStartupSequence() — Step 13 failed (non-fatal)",
        error
      );
    }

    // Tell the EAS model how to reach the player, so an alert arriving over
    // the WebSocket can preempt what is playing. Registered here rather than
    // at bootstrap because this module already sits downstream of both
    // graphs: a value import of VideoPlaybackController from the entry module
    // pulls the interactor barrel — and with it the whole WebSocket stack —
    // into `index.tsx`'s static graph, which initialises those modules before
    // their own dependencies and leaves them half-built.
    EamModelImpl.getInstance().setPlaybackProvider(
      (): VideoPlaybackController => VideoPlaybackController.getInstance()
    );

    // And how to read the market, which decides whether an alert can be
    // dismissed at all — a template with no "Press BACK to dismiss" footer
    // offers no dismissal, whatever the alert's own allowUserExit says.
    // Read at alert time rather than captured, so a later branding refresh
    // cannot leave the model on a stale market.
    EamModelImpl.getInstance().setEasDisplayModeProvider((): string =>
      selectEasDisplayMode(getState())
    );

    // Step 14 — connect the WebSocket, whose FSM GET_CLIENT_CERT delegate
    // validates and, when missing or expired, fetches the soft client cert.
    // That is the sequence's softClientCert step — it has no separate call.
    try {
      logStepStarted(PostSigninStartupStep.WebSocketConnection);
      Log.d(
        TAG,
        "postSigninStartupSequence() — Step 14: connecting WebSocket " +
          "(acquires softClientCert)"
      );
      await interactor.connectWebSocket();
      logStepCompleted(PostSigninStartupStep.WebSocketConnection);
      Log.d(TAG, "postSigninStartupSequence() — Step 14 complete");
    } catch (wsError: unknown) {
      logStepFailure(PostSigninStartupStep.WebSocketConnection, wsError);
      Log.e(TAG, "postSigninStartupSequence() — Step 14 failed", wsError);
    }

    // Ready video playback controller
    await VideoPlaybackController.getInstance().ready();

    completeStartup(
      failedSteps.length === 0
        ? PostSigninStartupResult.Success
        : PostSigninStartupResult.Degraded
    );

    return {
      inHome,
      inRegion,
      inBlacklist,
      requiresProfileSelection,
      isProfileGateResolved
    };
  },
  {
    // The sequence is dispatched from more than one place (SplashScreen on a
    // rehydrated token, ProtectedRoute on any authenticated entry). Each guards
    // with a per-mount ref only, which does not survive a remount, so a second
    // dispatch could re-run the whole chain — a duplicate deviceFeatureSearch,
    // branding bundle, channels fetch and so on — while the first is still in
    // flight. Startup runs once per session.
    condition: (
      _arg: TPostSigninStartupParams | undefined,
      { getState }
    ): boolean => {
      const state = getState().postSigninStartup;
      return !state.isStarting && !state.hasStartedUp;
    }
  }
);

/**
 * Escape hatch: re-boot the app.
 *
 * A reload is the recovery, not a cosmetic reset — it re-runs the whole
 * bootstrap including a fresh `GET /tsriEndpoints/{tsriId}`. A malformed
 * catalog entry corrected server-side is picked up here and nowhere else:
 * further retries in the running app keep resolving the same cached endpoint.
 */
export const recoverByReloading = createAsyncThunk<void, void>(
  "postSigninStartup/recoverByReloading",
  async (): Promise<void> => {
    Log.i(TAG, "recoverByReloading() — reloading to re-fetch TSRI catalog");
    window.location.reload();
  }
);

/**
 * Refreshes the in-home location status in response to a network change.
 * Non-fatal — always fulfils; on error sets all in-home fields to null.
 */
export const refreshInHomeLocation = createAsyncThunk<
  Pick<PostSigninStartupState, "inHome" | "inRegion" | "inBlacklist">,
  void
>("postSigninStartup/refreshInHomeLocation", async () => {
  const interactor: PostSigninInteractor = new PostSigninInteractor();
  try {
    Log.d(TAG, "refreshInHomeLocation() called");
    const result = await interactor.fetchInHomeLocation({
      msoPartnerId: await DeviceModel.getMsoPartnerId()
    });
    const inHome = result.inHome === undefined ? null : result.inHome;
    const inRegion = result.inRegion === undefined ? null : result.inRegion;
    const inBlacklist =
      result.inBlacklist === undefined ? null : result.inBlacklist;
    Log.d(TAG, "refreshInHomeLocation() complete");
    return { inHome, inRegion, inBlacklist };
  } catch (error: unknown) {
    Log.e(TAG, "refreshInHomeLocation() failed", error);
    return { inHome: null, inRegion: null, inBlacklist: null };
  }
});

const postSigninStartupSlice = createSlice({
  name: "postSigninStartup",
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(deviceFeaturesLoaded, (state) => {
      state.hasLoadedDeviceFeatures = true;
    });
    builder.addCase(brandingBundleLoaded, (state) => {
      state.hasLoadedBrandingBundle = true;
    });
    builder.addCase(stringOverridesLoaded, (state, action) => {
      state.hasLoadedStringOverrides = true;
      state.stringOverrides = action.payload;
    });
    builder.addCase(postSigninStartupSequence.pending, (state) => {
      state.isStarting = true;
      state.startupError = null;
      state.isProfileGateResolved = false;
      state.isLoadingInternalRatings = true;
      state.internalRatingsError = "";
    });
    builder.addCase(postSigninStartupSequence.fulfilled, (state, action) => {
      state.isStarting = false;
      state.hasStartedUp = !action.payload.requiresProfileSelection;
      state.inHome = action.payload.inHome;
      state.inRegion = action.payload.inRegion;
      state.inBlacklist = action.payload.inBlacklist;
      state.requiresProfileSelection = action.payload.requiresProfileSelection;
      state.isProfileGateResolved = action.payload.isProfileGateResolved;
      state.isLoadingInternalRatings = false;
      state.isInternalRatingsLoaded = true;
    });
    builder.addCase(postSigninStartupSequence.rejected, (state, action) => {
      state.isStarting = false;
      state.isProfileGateResolved = false;
      state.isLoadingInternalRatings = false;
      state.startupError =
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : "Post sign-in startup failed";
    });
    builder.addCase(refreshInHomeLocation.fulfilled, (state, action) => {
      state.inHome = action.payload.inHome;
      state.inRegion = action.payload.inRegion;
      state.inBlacklist = action.payload.inBlacklist;
    });
  }
});

export const selectIsStarting = (state: UnityRootState): boolean =>
  state.postSigninStartup.isStarting;

export const selectHasStartedUp = (state: UnityRootState): boolean =>
  state.postSigninStartup.hasStartedUp;

export const selectHasLoadedDeviceFeatures = (state: UnityRootState): boolean =>
  state.postSigninStartup.hasLoadedDeviceFeatures;

export const selectStartupError = (state: UnityRootState): string | null =>
  state.postSigninStartup.startupError;

export const selectInHome = (state: UnityRootState): boolean | null =>
  state.postSigninStartup.inHome;

export const selectInRegion = (state: UnityRootState): boolean | null =>
  state.postSigninStartup.inRegion;

export const selectInBlacklist = (state: UnityRootState): boolean | null =>
  state.postSigninStartup.inBlacklist;

export const selectRequiresProfileSelection = (
  state: UnityRootState
): boolean => state.postSigninStartup.requiresProfileSelection;

export const selectIsProfileGateResolved = (state: UnityRootState): boolean =>
  state.postSigninStartup.isProfileGateResolved;

export const selectHasLoadedStringOverrides = (
  state: UnityRootState
): boolean => state.postSigninStartup.hasLoadedStringOverrides;

export const selectStringOverrides = (
  state: UnityRootState
): Record<string, string> => state.postSigninStartup.stringOverrides;

// Internal ratings selectors
export const selectIsLoadingInternalRatings = (
  state: UnityRootState
): boolean => state.postSigninStartup.isLoadingInternalRatings;

export const selectIsInternalRatingsLoaded = (state: UnityRootState): boolean =>
  state.postSigninStartup.isInternalRatingsLoaded;

export const selectInternalRatingsError = (state: UnityRootState): string =>
  state.postSigninStartup.internalRatingsError;

export default postSigninStartupSlice.reducer;
